import { useEffect, useState } from "react";
import { Button, Card, CardContent, Container, Grid } from "@material-ui/core";
import StructureFields from "components/_refactor/fields/structure";
import ClearButton from "components/_refactor/button/clear";
import HeaderClientes from "components/_refactor/headers/clients";
import TableRelatorrioPedidos from "./components/TableRelatorioPedidos";
import PeriodField from "components/_refactor/fields/period-field";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";
import { IsMobile900, IsMobile } from "utils/is-mobile";
import { getUserId } from "utils/get-user-info";

function getPeriodDates(periodo) {
  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const start = new Date(end);
  start.setDate(end.getDate() - (periodo - 1));
  const pad = (n) => n.toString().padStart(2, "0");
  const format = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return {
    dtIni: format(start),
    dtFim: format(end),
  };
}

export default function RelatorioPedidos() {
  const userId = getUserId();
  const [structureLoading, setStructureLoading] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [periodo, setPeriodo] = useState(null);
  const [totalRegisters, setTotalRegisters] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(25);
  const [reportList, setReportList] = useState([]);
  const [structureFilters, setStructureFilters] = useState({});
  const [filters, setFilters] = useState({
    dtIni: "",
    dtFim: "",
  });

  const snackbar = useSnackbar();
  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const handlePeriodoChange = (option) => {
    setPeriodo(option);
    if (option && option.value !== "Outros") {
      const { dtIni, dtFim } = getPeriodDates(option.value);
      setFilters((prev) => ({
        ...prev,
        dtIni,
        dtFim,
      }));
    }
  };

  const handleDataInicialChange = (newValue) => {
    setFilters((prev) => ({
      ...prev,
      dtIni: newValue || "",
    }));
  };

  const handleDataFinalChange = (newValue) => {
    setFilters((prev) => ({
      ...prev,
      dtFim: newValue || "",
    }));
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    setPeriodo(null);
    setFilters({
      dtIni: "",
      dtFim: "",
    });
  };

  const getReportsList = async () => {
    setIsLoadingAll(true);
    await Api.get(
      "order-report-detail",
      "reportApp",
      "?idUsuario=" +
        userId +
        "&page=" +
        currentPage +
        "&pageSize=" +
        currentSizePerPage,
      true
    )
      .then((value) => {
        setReportList(value?.Result?.ListPedidos || []);
        setTotalRegisters(value?.Result?.TotalRegistros || 0);
        setCurrentPage(value?.Result?.Pagina || 0);
      })
      .catch((error) => {
        snackbar.showMessage(
          "Ocorreu um erro ao buscar os relatórios. Tente novamente.",
          {
            severity: "error",
          }
        );
      });
    setIsLoadingAll(false);
  };

  const handleGenerateReport = async () => {
    setIsLoadingAll(true);
    await Api.post(
      "order-report-create",
      "reportApp",
      {
        idLoja: structureFilters?.idLoja,
        ...(structureFilters?.idMarca && {
          idMarca: structureFilters?.idMarca,
        }),
        ...(structureFilters?.idCanal && {
          idCanal: structureFilters?.idCanal,
        }),
        ...(structureFilters?.idRepresentante && {
          idRepresentante: structureFilters?.idRepresentante,
        }),
        idGrupoEconomico: structureFilters?.idGrupoEconomico,
        dtIni: filters.dtIni,
        dtFim: filters.dtFim,
      },
      true
    )
      .then(() => {
        getReportsList();
      })
      .catch((error) => {
        snackbar.showMessage(
          "Ocorreu um erro ao gerar o relatório. Tente novamente.",
          {
            severity: "error",
          }
        );
      })
      .finally(() => {
        setIsLoadingAll(false);
      });
  };

  useEffect(() => {
    getReportsList();
  }, []);

  return (
    <>
      <HeaderClientes
        section="Relatório de Pedidos"
        subsection="Pedidos"
        tooltipMessage="Para garantir melhor performance e usabilidade, a extração deste relatório será realizada em segundo plano (background). Isso permite que você continue utilizando a ferramenta do Portal Karsten normalmente enquanto o relatório é processado. Assim que a geração for concluída, o arquivo ficará disponível para download na central de relatório de pedidos."
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <StructureFields
                fieldsOrder={["economicGroup", "store"]}
                clearFilters={clearFilters}
                onStructureFieldsChange={setStructureFilters}
                disableAll={isLoadingAll}
                onStructureFieldsLoad={setStructureLoading}
                requiredFields={["economicGroup", "store"]}
              />
              <Grid item xs={12} sm={periodo?.value === "Outros" ? 12 : 4}>
                <PeriodField
                  label="Período de Apuração"
                  value={periodo}
                  onChange={handlePeriodoChange}
                  initialDate={filters.dtIni}
                  finalDate={filters.dtFim}
                  setInitialDate={handleDataInicialChange}
                  setFinalDate={handleDataFinalChange}
                  disabled={isLoadingAll}
                  required
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2} style={{ marginTop: "1rem" }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    color="default"
                    variant="contained"
                    onClick={() => handleGenerateReport()}
                    style={
                      isMobile || isMobile900
                        ? {
                            width: "100%",
                            marginBottom: "0.75rem",
                            marginTop: "0.75rem",
                          }
                        : { marginBottom: "0rem" }
                    }
                    disabled={
                      isLoadingAll ||
                      !structureFilters?.idLoja ||
                      !structureFilters?.idGrupoEconomico ||
                      !filters.dtIni ||
                      !filters.dtFim ||
                      structureLoading.length > 0
                    }
                  >
                    {isLoadingAll ? "Gerando Relatório..." : "Gerar Relatório"}
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={isMobile ? 12 : isMobile900 ? 6 : 3}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <ClearButton
                    disable={isLoadingAll}
                    onClick={handleClearFilters}
                    fullWidth={true}
                  />
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Grid item xs={12}>
          <TableRelatorrioPedidos
            tableData={reportList}
            currentPage={currentPage}
            totalRegisters={totalRegisters}
            currentSizePerPage={currentSizePerPage}
            searching={false}
            onPageChange={setCurrentPage}
            onSizePerPageChange={setCurrentSizePerPage}
            extraActions={
              <Button
                color="default"
                variant="contained"
                onClick={() => getReportsList()}
              >
                {isLoadingAll ? "Atualizando..." : "Atualizar"}
              </Button>
            }
          />
        </Grid>
      </Container>
    </>
  );
}
